import { useRouter } from 'next/router';
import * as React from 'react';
import { useAnalytics } from 'src/utils/analytics-context';
import { SAVED_LANGUAGES } from 'src/utils/constants';
type Language = (typeof SAVED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE = 'js';
interface CodeBlockLanguageType {
  preferredLanguage: Language;
  setPreferredLanguage: (language: string) => void;
}
const CodeBlockLanguage = React.createContext<CodeBlockLanguageType | null>(null);
export const CodeBlockLanguageProvider: React.FC<{
  children?: React.ReactNode;
}> = ({
  children
}) => {
  const {
    query,
    isReady
  } = useRouter();
  const analytics = useAnalytics();
  const [preferredLanguage, setPreferredLanguage] = React.useState<Language>(DEFAULT_LANGUAGE);
  const savePreferredLanguage = React.useCallback((language: unknown) => {
    if (isValidLanguage(language)) {
      setPreferredLanguage(language);
      setLocalStorageLanguage(language);
      void analytics.track('User selected language', {
        language
      });
    }
  }, [analytics]);
  React.useEffect(() => {
    const localStorageLanguage = window.localStorage.getItem('@workos/language');
    savePreferredLanguage(localStorageLanguage);
  }, [savePreferredLanguage]);
  React.useEffect(() => {
    if (isReady) {
      const overrideLanguage = query.sdk;
      savePreferredLanguage(overrideLanguage);
    }
  }, [query.sdk, isReady, savePreferredLanguage]);
  return <CodeBlockLanguage.Provider value={{
    preferredLanguage,
    setPreferredLanguage: savePreferredLanguage
  }} data-sentry-element="CodeBlockLanguage.Provider" data-sentry-component="CodeBlockLanguageProvider" data-sentry-source-file="code-block-language-provider.tsx">
      {children}
    </CodeBlockLanguage.Provider>;
};
export const useCodeBlockLanguage = () => {
  const codeBlockLanguageContext = React.useContext(CodeBlockLanguage);
  if (!codeBlockLanguageContext) {
    throw new TypeError('useCodeBlockLanguage must be called from within a `CodeBlockLanguageProvider`.');
  }
  const {
    preferredLanguage,
    setPreferredLanguage
  } = codeBlockLanguageContext;
  return [preferredLanguage, setPreferredLanguage] as const;
};
const setLocalStorageLanguage = (language: Language) => {
  if (typeof window === 'object') {
    window.localStorage.setItem('@workos/language', language);
  }
};
const isValidLanguage = (language: unknown): language is Language =>
// Introducing lint rule banning type assertions
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
SAVED_LANGUAGES.includes(language as Language);